<template>
  <div class="space-y-5">
    <section class="overflow-hidden rounded-[24px] border border-[#eadfd4] bg-[linear-gradient(135deg,#fffdfb_0%,#fff6ee_100%)] shadow-sm">
      <div class="flex flex-col gap-5 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
        <div class="flex min-w-0 items-center gap-4">
          <span class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-3xl text-brand shadow-sm ring-1 ring-[#eadfd4] sm:h-16 sm:w-16">
            <i class="ri-settings-4-fill"></i>
          </span>
          <div class="min-w-0">
            <p class="text-xs font-semibold uppercase tracking-wide text-brand">Vendor account</p>
            <h2 class="mt-1 truncate text-[1.55rem] font-semibold leading-tight text-ink sm:text-[1.9rem]">
              Settings
            </h2>
            <p class="mt-1 max-w-2xl text-sm leading-6 text-muted">
              Manage profile details, password security, help pages, and account actions.
            </p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
          <RouterLink
            to="/dashboard/settings/profile"
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
          >
            <i class="ri-user-settings-fill text-lg"></i>
            Profile
          </RouterLink>
          <RouterLink
            to="/dashboard/settings/change-password"
            class="inline-flex items-center justify-center gap-2 rounded-xl border border-[#e2d6ca] bg-white px-4 py-2.5 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
          >
            <i class="ri-lock-password-fill text-lg"></i>
            Password
          </RouterLink>
        </div>
      </div>
    </section>

    <section class="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)]">
      <div class="rounded-[24px] border border-[#eee4da] bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-base font-semibold text-ink">Account</h3>
            <p class="mt-1 text-sm text-muted">Update your vendor details and sign-in security.</p>
          </div>
          <span class="hidden h-10 w-10 items-center justify-center rounded-2xl bg-[#fff4e8] text-xl text-brand sm:flex">
            <i class="ri-shield-user-fill"></i>
          </span>
        </div>

        <div class="mt-4 grid gap-3 sm:grid-cols-2">
          <RouterLink
            v-for="item in accountItems"
            :key="item.label"
            :to="item.to"
            class="group flex min-h-[112px] items-start justify-between gap-3 rounded-2xl border border-[#eadfd4] bg-[#fffaf5] p-4 text-left transition hover:-translate-y-0.5 hover:border-brand/70 hover:shadow-[0_14px_30px_rgba(91,70,56,0.10)]"
          >
            <span class="flex min-w-0 gap-3">
              <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-2xl text-brand ring-1 ring-[#eadfd4] transition group-hover:bg-brand group-hover:text-white">
                <i :class="item.icon"></i>
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-semibold text-ink">{{ item.label }}</span>
                <span class="mt-1 block text-xs leading-5 text-muted">{{ item.description }}</span>
              </span>
            </span>
            <i class="ri-arrow-right-up-line shrink-0 text-xl text-[#b79d85] transition group-hover:text-brand"></i>
          </RouterLink>
        </div>
      </div>

      <aside class="rounded-[24px] border border-[#eee4da] bg-white p-4 shadow-sm sm:p-5">
        <div class="flex items-center gap-3">
          <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff4e8] text-2xl text-brand">
            <i class="ri-customer-service-2-fill"></i>
          </span>
          <div>
            <h3 class="text-base font-semibold text-ink">Help desk</h3>
            <p class="text-sm text-muted">Support and product information.</p>
          </div>
        </div>

        <div class="mt-4 grid gap-2">
          <RouterLink
            v-for="item in infoItems"
            :key="item.label"
            :to="item.to"
            class="group flex items-center justify-between gap-3 rounded-2xl border border-transparent px-3 py-3 text-sm font-semibold text-ink transition hover:border-[#eadfd4] hover:bg-[#fffaf5]"
          >
            <span class="flex min-w-0 items-center gap-3">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fff4e8] text-xl text-brand">
                <i :class="item.icon"></i>
              </span>
              <span class="truncate">{{ item.label }}</span>
            </span>
            <i class="ri-arrow-right-s-line text-xl text-[#b79d85] transition group-hover:text-brand"></i>
          </RouterLink>
        </div>
      </aside>
    </section>

    <section class="rounded-[24px] border border-[#f0d9d9] bg-white p-4 shadow-sm sm:p-5">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start gap-3">
          <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-2xl text-red-600">
            <i class="ri-error-warning-fill"></i>
          </span>
          <div>
            <h3 class="text-base font-semibold text-ink">Account actions</h3>
            <p class="mt-1 text-sm leading-6 text-muted">Logout from this device or request account removal.</p>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-xl border border-[#e2d6ca] bg-white px-4 py-2.5 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="logout"
          >
            <i class="ri-logout-box-r-fill text-lg"></i>
            Logout
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:border-red-200 hover:bg-red-100"
            @click="showDeleteNotice"
          >
            <i class="ri-delete-bin-6-fill text-lg"></i>
            Delete account
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { RouterLink, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { clearAuth } from '@/services/auth'

const router = useRouter()

const accountItems = [
  {
    label: 'Profile',
    description: 'Edit contact details, business name, and vendor profile information.',
    icon: 'ri-user-settings-fill',
    to: '/dashboard/settings/profile'
  },
  {
    label: 'Change Password',
    description: 'Update your password and generate a stronger replacement.',
    icon: 'ri-lock-password-fill',
    to: '/dashboard/settings/change-password'
  }
]

const infoItems = [
  { label: 'About Invi8', icon: 'ri-information-fill', to: '/dashboard/settings/about' },
  { label: 'Support', icon: 'ri-customer-service-2-fill', to: '/dashboard/settings/support' },
  { label: 'FAQs', icon: 'ri-question-answer-fill', to: '/dashboard/settings/faqs' },
  { label: 'Terms of Use', icon: 'ri-file-list-3-fill', to: '/dashboard/settings/terms' },
  { label: 'Privacy Policy', icon: 'ri-shield-check-fill', to: '/dashboard/settings/privacy' }
]

const logout = async () => {
  const result = await Swal.fire({
    title: 'Logout?',
    text: 'Your vendor session will be cleared on this device.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#c73d3d',
    cancelButtonColor: '#8c7b6b',
    confirmButtonText: 'Logout',
    cancelButtonText: 'Cancel',
    reverseButtons: true
  })
  if (!result.isConfirmed) return
  clearAuth()
  router.push('/login?role=vendor')
}

const showDeleteNotice = () => {
  Swal.fire({
    icon: 'info',
    title: 'Delete account',
    text: 'Account deletion is handled by support. Please contact support to verify and process the request.',
    confirmButtonColor: '#FF7A00'
  })
}
</script>
